import { yupResolver } from '@hookform/resolvers/yup'
import { Nullable } from '@if/core/types'
import { ControlledDateOnlyPicker, ControlledTextField } from '@if/mui'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import { InputTextField, InputTextFieldWithMask } from '@src/components'
import { EMask } from '@src/data/types'
import { type BanishmentForm, banishmentFormSchema } from '@src/data/types/BanSchema'
import { memo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  onConfirm: (data: BanishmentForm) => void
  onCancel: () => void
  defaultValues?: Partial<BanishmentForm>
  isLoading?: boolean
  readonly?: {
    firstName?: boolean
    lastName?: boolean
    birthDate?: boolean
    sin?: boolean
  }
}

const BanishmentFormDialog = ({
  open,
  onConfirm,
  onCancel,
  defaultValues = {},
  isLoading = false,
  readonly = {},
}: Props) => {
  const { t } = useTranslation()

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      birthDate: null,
      sin: '',
      reason: '',
      expirationDate: null,
      ...defaultValues,
    } satisfies Nullable<BanishmentForm> as unknown as BanishmentForm,
    resolver: yupResolver(banishmentFormSchema),
    mode: 'onBlur',
  })

  const handleFormSubmit = (data: BanishmentForm) => {
    const cleanedData = {
      ...data,
      expirationDate: data.expirationDate || null,
    }
    onConfirm(cleanedData)
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('ban.banButtonLabel')}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ControlledTextField
                name="firstName"
                control={control}
                label={t('editCreditApplication.information.firstName')}
                fullWidth
                disabled={readonly.firstName}
                variant="outlined"
              />
              <ControlledTextField
                name="lastName"
                control={control}
                label={t('editCreditApplication.information.lastName')}
                fullWidth
                disabled={readonly.lastName}
                variant="outlined"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <ControlledDateOnlyPicker
                name="birthDate"
                control={control}
                label={t('editCreditApplication.information.birthDate')}
                disabled={readonly.birthDate}
                sx={{ flex: 1 }}
              />
              <Controller
                name="sin"
                control={control}
                render={({ field }) => (
                  <InputTextFieldWithMask
                    mask={EMask.sinMask}
                    label={t('editCreditApplication.information.sin')}
                    {...field}
                    disabled={readonly.sin}
                    sx={{ flex: 1 }}
                    error={errors.sin}
                  />
                )}
              />
            </Box>

            <InputTextField
              label={t('ban.reason')}
              multiline
              rows={4}
              fullWidth
              {...register('reason')}
              error={errors.reason}
              variant="outlined"
            />

            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                {t('ban.leaveEmptyMessage')}
              </Typography>
              <ControlledDateOnlyPicker
                name="expirationDate"
                control={control}
                label={t('ban.endPeriod')}
                sx={{ width: '50%' }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="error" disabled={!isValid || isLoading}>
            {t('ban.banButtonLabel')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default memo(BanishmentFormDialog)
